import React, { useRef } from 'react';
import {
    normalizePlateDigits,
    normalizePlateLetter,
    parsePlateSearchInput,
} from '../../utils/licensePlateSearch';

const getCopy = (fields = {}) => ({
    plate: fields.plate || 'Plate Number',
    helper: fields.searchHelper || 'Enter the plate number and Arabic letters. You can paste the full plate.',
    numberLabel: fields.plateNumbers || 'Plate numbers',
    letter1Label: fields.plateLetter1 || 'First letter',
    letter2Label: fields.plateLetter2 || 'Second letter',
    letter3Label: fields.plateLetter3 || 'Third letter',
    search: fields.search || 'Search',
    clear: fields.clearSearch || 'Clear search',
});

function VehicleSearchForm({
    onSearch,
    onClear,
    plateLetters,
    setPlateLetters,
    plateNumber,
    setPlateNumber,
    isLangError,
    langErrorMessage,
    isNumError,
    numErrorMessage,
    isSearching = false,
    statusMessage = '',
    fields,
    isRtl = false,
    theme = 'light',
    canClearSearch = false,
}) {
    const copy = getCopy(fields);
    const numberRef = useRef(null);
    const letterRefs = useRef([]);
    const hasInput = Boolean(plateNumber || (plateLetters || []).some(Boolean));
    const canClear = hasInput || canClearSearch;

    const focusLetter = (index) => {
        letterRefs.current[index]?.focus();
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!isSearching) onSearch();
    };

    const handlePaste = (event) => {
        const text = event.clipboardData.getData('text');
        const parsedPlate = parsePlateSearchInput(text);
        const parsedLetterCount = parsedPlate.plateLetters.filter(Boolean).length;

        if (text.trim().length <= 1 || (!parsedPlate.plateNumber && parsedLetterCount <= 1)) {
            return;
        }

        event.preventDefault();
        if (parsedPlate.plateNumber) setPlateNumber(parsedPlate.plateNumber);
        if (parsedLetterCount > 0) setPlateLetters(parsedPlate.plateLetters);

        if (parsedPlate.plateNumber.length >= 3 && parsedLetterCount >= 2) {
            letterRefs.current[Math.min(parsedLetterCount, 3) - 1]?.focus();
        }
    };

    const handleNumberChange = (event) => {
        const normalizedNumber = normalizePlateDigits(event.target.value).slice(0, 4);
        setPlateNumber(normalizedNumber);
        if (normalizedNumber.length >= 4) focusLetter(0);
    };

    const handleLetterChange = (index, value) => {
        const nextLetters = [...(plateLetters || ['', '', ''])];
        const normalizedLetter = normalizePlateLetter(value);
        nextLetters[index] = normalizedLetter;
        setPlateLetters(nextLetters);

        if (normalizedLetter && index < 2) {
            focusLetter(index + 1);
        }
    };

    const handleLetterKeyDown = (index, event) => {
        if (event.key !== 'Backspace' || event.currentTarget.value) return;

        event.preventDefault();
        if (index === 0) {
            numberRef.current?.focus();
            return;
        }
        focusLetter(index - 1);
    };

    const handleNumberKeyDown = (event) => {
        if (event.key === 'ArrowRight' && plateNumber.length >= 3) {
            event.preventDefault();
            focusLetter(0);
        }
    };

    const themeClass = theme === 'dark' ? 'dark-theme' : 'light-theme';

    return (
        <form className={`vehicle-search-form ${themeClass} ${isRtl ? 'rtl' : ''}`} onSubmit={handleSubmit} onPaste={handlePaste} noValidate>
            <fieldset className="vehicle-search-fieldset" disabled={isSearching}>
                <legend className="vehicle-search-legend">{copy.plate}</legend>
                <p id="plate-search-helper" className="vehicle-search-helper vehicle-search-visually-hidden">{copy.helper}</p>

                <div className="vehicle-search-row">
                    <label className="vehicle-search-field vehicle-search-number-field" htmlFor="numbers">
                        <span>{copy.numberLabel}</span>
                        <input
                            ref={numberRef}
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            className={isNumError ? 'is-invalid' : ''}
                            id="numbers"
                            maxLength="4"
                            value={plateNumber}
                            onKeyDown={handleNumberKeyDown}
                            onChange={handleNumberChange}
                            aria-describedby={`plate-search-helper${isNumError ? ' numberError' : ''}`}
                            aria-invalid={isNumError}
                        />
                    </label>

                    <div className="vehicle-search-letter-group" role="group" aria-describedby={`plate-search-helper${isLangError ? ' letterError' : ''}`} aria-label={copy.plate}>
                        {[copy.letter1Label, copy.letter2Label, copy.letter3Label].map((label, index) => (
                            <label className="vehicle-search-field vehicle-search-letter-field" htmlFor={`let${index + 1}`} key={`let${index + 1}`}>
                                <span>{label}</span>
                                <input
                                    ref={(element) => { letterRefs.current[index] = element; }}
                                    type="text"
                                    inputMode="text"
                                    autoComplete="off"
                                    className={isLangError ? 'is-invalid' : ''}
                                    id={`let${index + 1}`}
                                    maxLength="1"
                                    value={plateLetters?.[index] || ''}
                                    onKeyDown={(event) => handleLetterKeyDown(index, event)}
                                    onChange={(event) => handleLetterChange(index, event.target.value)}
                                    aria-invalid={isLangError}
                                />
                            </label>
                        ))}
                    </div>

                    <div className="vehicle-search-actions">
                        <button
                            className="vehicle-search-submit"
                            id="search"
                            type="submit"
                            disabled={isSearching}
                            aria-label={copy.search}
                        >
                            {isSearching ? (
                                <span className="vehicle-search-spinner" aria-hidden="true"></span>
                            ) : (
                                <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                            )}
                            <span>{copy.search}</span>
                        </button>
                        <button className="vehicle-search-clear" type="button" onClick={onClear} disabled={isSearching || !canClear}>
                            {copy.clear}
                        </button>
                    </div>
                </div>

                <div className="vehicle-search-feedback">
                    {isNumError && <span id="numberError" className="vehicle-search-error" role="alert">{numErrorMessage}</span>}
                    {isLangError && <span id="letterError" className="vehicle-search-error" role="alert">{langErrorMessage}</span>}
                    {statusMessage && <span className="vehicle-search-status" role="status" aria-live="polite">{statusMessage}</span>}
                </div>
            </fieldset>
        </form>
    );
}

export default VehicleSearchForm;
